/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { difference, flow, identity, omit, pickBy } from 'lodash';

import { omitTypename } from '@/graphql/utils/fields';
import { USER_LISTING_PREFERENCES_QUERY } from '@/graphql/listing/user_preferences/queries';

import { saveUserListingPreferences, deleteUserListingPreferences } from './mutations';
import { UserPreferences } from './types';
import {
  convertAttributes,
  convertColumnSizes,
  convertFilters,
  convertSort,
  filterInvalidAttributes,
  filterInvalidColumnSizes,
  toDefaultSortIfInvalid,
} from '../../utils';

// Necessary conversion of attrs since ac_groups flag toggling toggles between 'owners' and 'ownersGroup' attrs
const convertACGroupsToFrom = (convertToGroups: boolean) => {
  const acGroupsAttrsToGroups = {
    owners: 'ownersGroup',
  };
  const acGroupsAttrsFromGroups = {
    ownersGroup: 'owners',
  };

  return convertToGroups ? acGroupsAttrsToGroups : acGroupsAttrsFromGroups;
};

export const validateUserPreferences = (userPreferencesData, validColumns, attrsToFrom, defaultSort) => {
  const validateColumns = (columns, validColumns, attrsToFrom) =>
    flow(
      columns => convertAttributes(columns, attrsToFrom),
      columns => filterInvalidAttributes(columns, validColumns)
    )(columns);
  const validateColumnSizes = (columnSizes, validColumns, attrsToFrom) =>
    flow(
      columnSizes => convertColumnSizes(columnSizes, attrsToFrom),
      columnSizes => filterInvalidColumnSizes(columnSizes, validColumns)
    )(columnSizes);
  const validateSort = (sort, validColumns, defaultSort, attrsToFrom) =>
    flow(
      sort => convertSort(sort, attrsToFrom),
      sort => toDefaultSortIfInvalid(sort, validColumns, defaultSort)
    )(sort);
  const validateFilters = (filters, attrsToFrom) => convertFilters(filters, attrsToFrom);

  if (userPreferencesData.columns) {
    userPreferencesData.columns = validateColumns(userPreferencesData.columns, validColumns, attrsToFrom);
  }
  if (userPreferencesData.columnSizes) {
    userPreferencesData.columnSizes = validateColumnSizes(userPreferencesData.columnSizes, validColumns, attrsToFrom);
  }
  if (userPreferencesData.sort) {
    userPreferencesData.sort = validateSort(userPreferencesData.sort, validColumns, defaultSort, attrsToFrom);
  }
  if (userPreferencesData.filters) {
    userPreferencesData.filters = validateFilters(userPreferencesData.filters, attrsToFrom);
  }

  return userPreferencesData;
};

/**
 * User preferences functionality for SorListing component.
 *
 * A mixin is good enough until we get the Composition API available, as there
 * are only a few dependencies between this mixin and the component.
 */
export const userPreferencesMixin = {
  props: {
    userPreferences: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      persistedUserPreferences: {} as UserPreferences,

      userPreferencesData: {},
      lastChangedUserPreference: null,
    };
  },
  watch: {
    userPreferencesData(newUserPreferences: object, oldUserPreferences: object): void {
      this.persistUserPreferences(newUserPreferences, oldUserPreferences);

      this.$emit('userPreferencesChange', newUserPreferences);
    },
    persistedUserPreferences(persistedUserPreferences: object): void {
      const userPreferencesData = this.combineUserPreferences(this.userPreferences, persistedUserPreferences);

      const validColumns = new Set(Object.keys(this.attributeMeta));
      // TODO: remove flag and converting attrs after ac_groups is finished
      const featureFlag = this.featureFlagState('ac_groups');

      this.userPreferencesData = validateUserPreferences(
        userPreferencesData,
        validColumns,
        convertACGroupsToFrom(featureFlag),
        this.defaultSort
      );
    },
  },
  methods: {
    notifyFetchUserPreferencesFail(showAlert): void {
      showAlert({
        type: 'error',
        message: 'Something went wrong fetching your user preferences.',
      });
    },
    notifySaveUserPreferencesFail(showAlert): void {
      showAlert({
        type: 'error',
        message: `Unable to update your user preferences.`,
      });
    },
    notifyDeleteUserPreferencesFail(showAlert): void {
      showAlert({
        type: 'error',
        message: `Unable to delete your user preferences.`,
      });
    },

    handleFetchUserPreferencesFail(showAlert): void {
      this.notifyFetchUserPreferencesFail(showAlert);
      this.setFallbackUserPreferences();
    },

    setFallbackUserPreferences(): void {
      this.userPreferencesData = this.userPreferences;
    },
    setUserPreferences(preferences): void {
      this.userPreferencesData = preferences;
    },
    setUserPreference(prefKey: string, prefValue: string): void {
      this.lastChangedUserPreference = prefKey;

      this.userPreferencesData = prefValue
        ? (this.userPreferencesData = {
            ...this.userPreferencesData,
            ...{ [prefKey]: prefValue },
          })
        : omit(this.userPreferencesData, prefKey);
    },

    combineUserPreferences(rawInputUserPreferences: object, rawPersistedUserPreferences: object): object {
      // Removes preferences which have null value
      const inputUserPreferences: object = pickBy(rawInputUserPreferences, identity);
      const persistedUserPreferences: object = pickBy(rawPersistedUserPreferences, identity);

      // Preferences in input user preferences are authoritative
      if (Object.keys(inputUserPreferences).length) return inputUserPreferences;
      return persistedUserPreferences;
    },
    // Differences between new and old are at most by one property
    // Note: If in the future we need to handle multiple changed keys at once,
    //       we should use the `deep-diff` package.
    persistUserPreferences(newUserPreferences: object, oldUserPreferences: object): void {
      const newPreferences: string[] = Object.keys(newUserPreferences);
      const oldPreferences: string[] = Object.keys(oldUserPreferences);

      const deletedPreferences: string[] = difference(oldPreferences, newPreferences);

      if (deletedPreferences.length && newPreferences.length < oldPreferences.length) {
        this.deletePersistedUserPreferences(deletedPreferences);
      } else if (newPreferences.length) {
        this.savePersistedUserPreferences(newUserPreferences);
      }
    },

    async savePersistedUserPreferences(userPreferences) {
      const handleResult = (result, error): void => {
        if (error || !result) {
          this.notifySaveUserPreferencesFail(this.showAlert);
        }
      };

      const prepareVariables = (state, userPreferences) => {
        const validSort = sort => !!(sort && sort.by && sort.direction);
        const sort = validSort(userPreferences.sort) ? userPreferences.sort : null;

        const filters = userPreferences.filters ? JSON.stringify(userPreferences.filters) : null;

        return {
          state,
          userPreferences: {
            pageSize: userPreferences.pageSize,
            columns: userPreferences.columns,
            columnSizes: userPreferences.columnSizes,
            sort,
            filters,
          },
        };
      };

      const variables = prepareVariables(this.state, userPreferences);
      const { result, error } = await saveUserListingPreferences(this.$apollo, variables);

      handleResult(result, error);

      return { result, error };
    },
    async deletePersistedUserPreferences(userPreferences: string[]) {
      const handleResult = (result, error): void => {
        if (error || !result) {
          this.notifyDeleteUserPreferencesFail(this.showAlert);
        }
      };

      const prepareVariables = (state, userPreferences) => {
        const userPreferencesToDelete = [];

        // TODO: do this cleaner
        if (userPreferences.includes('pageSize')) {
          userPreferencesToDelete.push('PAGE_SIZE');
        }
        if (userPreferences.includes('columns')) {
          userPreferencesToDelete.push('COLUMNS');
        }
        if (userPreferences.includes('columnSizes')) {
          userPreferencesToDelete.push('COLUMN_SIZES');
        }
        if (userPreferences.includes('sort')) {
          userPreferencesToDelete.push('SORT');
        }
        if (userPreferences.includes('filters')) {
          userPreferencesToDelete.push('FILTERS');
        }

        return {
          state: this.state,
          userPreferencesToDelete,
        };
      };

      const variables = prepareVariables(this.state, userPreferences);
      const { result, error } = await deleteUserListingPreferences(this.$apollo, variables);

      handleResult(result, error);

      return { result, error };
    },
  },
  apollo: {
    persistedUserPreferences: {
      fetchPolicy: 'network-only',
      query: USER_LISTING_PREFERENCES_QUERY,
      variables() {
        return { state: this.state };
      },
      update: function(data): object {
        const colSizes = data.currentUser.listingPreferences.columnSizes || [];

        let filters = data.currentUser.listingPreferences.filters;
        if (filters) filters = JSON.parse(filters);

        return {
          pageSize: data.currentUser.listingPreferences.pageSize,
          columns: data.currentUser.listingPreferences.columns,
          columnSizes: colSizes.map(c => {
            delete c.__typename;
            return c;
          }),
          sort: omitTypename(data.currentUser.listingPreferences.sort),
          filters,
        };
      },
      error() {
        this.handleFetchUserPreferencesFail(this.showAlert);
      },
    },
  },
};
